import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface OnboardingProgressResponse {
  user_entity_ref: string;
  role_id: string | null;
  completed_journeys: string[];
  nps_completed?: boolean;
}

export interface NpsAnswers {
  q1ClearExplanations: number;
  q2DurationPace: number;
  q3UsefulContent: number;
  q4VideosDiagrams: number;
  q5PlaygroundExperience: number;
  q6ConfidentIndependently: number;
  q7ReducedTime: string;
  q8ContentFormats: string[];
  q9Improvements?: string;
  q10NpsScore: number;
  q11ScoreReason?: string;
}

export interface NpsResponse {
  id: number;
  q1ClearExplanations: number;
  q2DurationPace: number;
  q3UsefulContent: number;
  q4VideosDiagrams: number;
  q5PlaygroundExperience: number;
  q6ConfidentIndependently: number;
  q7ReducedTime: string;
  q8ContentFormats: string[];
  q9Improvements: string | null;
  q10NpsScore: number;
  q11ScoreReason: string | null;
  submittedAt: string;
}

export interface OnboardingApiClient {
  getProgress(): Promise<OnboardingProgressResponse>;
  selectRole(roleId: string): Promise<{ role_id: string; completed_journeys: string[] }>;
  completeJourney(journeyId: string, roleId?: string): Promise<{ completed_journeys: string[] }>;
  submitNps(answers: NpsAnswers): Promise<{ nps_completed: boolean }>;
  getNpsResponses(): Promise<NpsResponse[]>;
}

export function createOnboardingApiClient(options: {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}): OnboardingApiClient {
  const { discoveryApi, fetchApi } = options;

  const getBaseUrl = () => discoveryApi.getBaseUrl('onboarding');

  return {
    async getProgress() {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(`${baseUrl}/progress`);
      if (!response.ok) {
        throw new Error(`Failed to fetch onboarding progress: ${response.statusText}`);
      }
      return response.json() as Promise<OnboardingProgressResponse>;
    },

    async selectRole(roleId: string) {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(`${baseUrl}/progress/role`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roleId }),
      });
      if (!response.ok) {
        throw new Error(`Failed to save role: ${response.statusText}`);
      }
      return response.json() as Promise<{ role_id: string; completed_journeys: string[] }>;
    },

    async completeJourney(journeyId: string, roleId?: string) {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(`${baseUrl}/progress/complete-journey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ journeyId, roleId }),
      });
      if (!response.ok) {
        throw new Error(`Failed to complete journey: ${response.statusText}`);
      }
      return response.json() as Promise<{ completed_journeys: string[] }>;
    },

    async submitNps(answers: NpsAnswers) {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(`${baseUrl}/progress/complete-nps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      if (!response.ok) {
        throw new Error(`Failed to submit NPS: ${response.statusText}`);
      }
      return response.json() as Promise<{ nps_completed: boolean }>;
    },

    async getNpsResponses() {
      const baseUrl = await getBaseUrl();
      const response = await fetchApi.fetch(`${baseUrl}/nps-responses`);
      if (!response.ok) {
        throw new Error(`Failed to fetch NPS responses: ${response.statusText}`);
      }
      return response.json() as Promise<NpsResponse[]>;
    },
  };
}
